import React from 'react';
import {
  AxisControls,
  Controls,
  Select,
  SelectLabel,
  ViewButton,
  ViewControls,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/styles';
import {
  ChartView,
  TableViewType,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/types';

interface ChartViewOption {
  label: string;
  value: ChartView;
}

interface VisualizationControlsProps {
  activeView: TableViewType;
  availableChartViews: ChartViewOption[];
  availableXColumnIndexes: number[];
  canShowChart: boolean;
  chartNumericColumnIndexes: number[];
  headers: string[];
  onSelectView: (view: TableViewType) => void;
  onSelectXColumnIndex: (columnIndex: number) => void;
  onSelectYColumnIndex: (columnIndex: number) => void;
  orderedColumnIndexes: number[];
  selectedXColumnIndex: number;
  selectedYColumnIndex: number;
}

const getChartButtonTitle = (canShowChart: boolean, label: string) =>
  canShowChart
    ? `Show ${label.toLowerCase()} chart`
    : `A chart requires a numeric column`;

export function VisualizationControls({
  activeView,
  availableChartViews,
  availableXColumnIndexes,
  canShowChart,
  chartNumericColumnIndexes,
  headers,
  onSelectView,
  onSelectXColumnIndex,
  onSelectYColumnIndex,
  orderedColumnIndexes,
  selectedXColumnIndex,
  selectedYColumnIndex,
}: VisualizationControlsProps) {
  return (
    <Controls>
      <ViewControls aria-label="Data visualization">
        <ViewButton
          type="button"
          $active={activeView === `table`}
          aria-pressed={activeView === `table`}
          onClick={() => onSelectView(`table`)}
        >
          Table
        </ViewButton>
        {availableChartViews.map((chartView) => (
          <ViewButton
            key={chartView.value}
            type="button"
            $active={activeView === chartView.value}
            aria-pressed={activeView === chartView.value}
            disabled={!canShowChart}
            title={getChartButtonTitle(canShowChart, chartView.label)}
            onClick={() => onSelectView(chartView.value)}
          >
            {chartView.label}
          </ViewButton>
        ))}
      </ViewControls>

      {activeView !== `table` && (
        <AxisControls>
          <SelectLabel>
            X axis
            <Select
              value={selectedXColumnIndex}
              onChange={(event) =>
                onSelectXColumnIndex(Number(event.target.value))
              }
            >
              {availableXColumnIndexes.map((columnIndex) => (
                <option key={columnIndex} value={columnIndex}>
                  {headers[columnIndex]}
                </option>
              ))}
            </Select>
          </SelectLabel>
          <SelectLabel>
            Y axis
            <Select
              value={selectedYColumnIndex}
              onChange={(event) =>
                onSelectYColumnIndex(Number(event.target.value))
              }
            >
              {orderedColumnIndexes
                .filter((columnIndex) =>
                  chartNumericColumnIndexes.includes(columnIndex),
                )
                .map((columnIndex) => (
                  <option key={columnIndex} value={columnIndex}>
                    {headers[columnIndex]}
                  </option>
                ))}
            </Select>
          </SelectLabel>
        </AxisControls>
      )}
    </Controls>
  );
}
